import { CASE_CONCLUSION_PRESETS } from '@/features/air/case-conclusion-preset'

import { toDisplayName } from '@/shared/parsing/utils/text'

import type { InvestigationSummary, Verdict } from '../types'
import type { Tone } from '@/components/common/pill/tone'
import type { FilterValueOption } from '@/shared/data-table/types/filters'

export type InvestigationChipMeta = {
	label: string
	tone: Tone
	confidencePct: number | null
	sortValue: string
}

type ChipDisplay = Pick<InvestigationChipMeta, 'label' | 'tone' | 'sortValue'>

/**
 * The two verdicts AIR® can actually reach — exhaustive over `Verdict`, so a
 * backend enum member added later is a compile error here (ADR 0020 §5).
 *
 * Label and tone are **read from** `CASE_CONCLUSION_PRESETS`, the Platform-root
 * owner, rather than restated; only `sortValue` is this list's own. Three
 * surfaces render a verdict now — this chip, the Behavior banner and the
 * overview's breakdown donut — and a third hand-written copy is how one of them
 * ends up a different colour from the other two.
 *
 * Deliberately holds NOTHING else: the "no verdict" case is not a third member,
 * it lives in {@link NO_VERDICT_CHIP_META}. See its comment for why.
 */
const VERDICT_CHIP_META: Record<Verdict, ChipDisplay> = {
	benign: { ...CASE_CONCLUSION_PRESETS.presets.benign, sortValue: '5 Benign' },
	malicious: {
		...CASE_CONCLUSION_PRESETS.presets.malicious,
		sortValue: '6 Malicious',
	},
}

/**
 * A finished investigation that produced **no** verdict — the ABSENCE of a
 * conclusion, not a third kind of conclusion (ADR 0020 §6).
 *
 * ADR 0020 §6 carries the backend evidence and the reasoning; the short version
 * is that a conclusion cannot exist without a verdict, so `verdict: null` means
 * there is no conclusion — never "AIR® was unsure". Grey rather than amber for
 * the same reason, and it sorts below both verdicts as the least actionable
 * terminal state.
 */
const NO_VERDICT_CHIP_META: ChipDisplay = {
	label: 'No Verdict',
	tone: 'neutral',
	sortValue: '4 No Verdict',
}

const RUN_STATUS_META: Record<
	Exclude<InvestigationSummary['status'], 'completed'>,
	Pick<InvestigationChipMeta, 'label' | 'tone' | 'sortValue'>
> = {
	// `pending` is grey, not amber: a queued run has not started, so it is inert
	// rather than a warning. The activity records agree and the app
	// has exactly one blue, which goes to the live state — `running`.
	pending: { label: 'Pending', tone: 'neutral', sortValue: '1 Pending' },
	running: { label: 'Running', tone: 'pending', sortValue: '2 Running' },
	paused: { label: 'Paused', tone: 'neutral', sortValue: '3 Paused' },
	failed: { label: 'Failed', tone: 'error', sortValue: '7 Failed' },
}

/**
 * The case-list endpoint's `investigation` filter values. A Case has exactly one
 * of them: no investigation, a run status, or — once completed — a verdict or
 * the absence of one (ADR 0020 §6).
 */
export const INVESTIGATION_FILTER_VALUES = [
	'none',
	'pending',
	'running',
	'paused',
	'no_verdict',
	'benign',
	'malicious',
	'failed',
] as const

export type InvestigationFilterValue =
	(typeof INVESTIGATION_FILTER_VALUES)[number]

/**
 * The Investigation filter's 8 options. `value` is the API value, so the URL
 * already holds what the endpoint accepts; labels are read from the chip
 * records so the dropdown reads like the column it filters.
 */
export const INVESTIGATION_FILTER_OPTIONS = [
	{ value: 'none', label: 'No Investigation' },
	{ value: 'pending', label: RUN_STATUS_META.pending.label },
	{ value: 'running', label: RUN_STATUS_META.running.label },
	{ value: 'paused', label: RUN_STATUS_META.paused.label },
	{ value: 'no_verdict', label: NO_VERDICT_CHIP_META.label },
	{ value: 'benign', label: VERDICT_CHIP_META.benign.label },
	{ value: 'malicious', label: VERDICT_CHIP_META.malicious.label },
	{ value: 'failed', label: RUN_STATUS_META.failed.label },
] as const satisfies ReadonlyArray<
	FilterValueOption & { value: InvestigationFilterValue }
>

/**
 * Converts an investigation confidence score from the API's 0..1 range into an
 * integer display percentage.
 *
 * @param confidence - Raw confidence score, or null/undefined when absent.
 */
export function investigationConfidencePct(
	confidence: number | null | undefined,
): number | null {
	if (
		confidence === null ||
		confidence === undefined ||
		!Number.isFinite(confidence)
	) {
		return null
	}
	return Math.round(confidence * 100)
}

/**
 * Resolves the list-table display state for a case investigation summary.
 * Completed investigations show their verdict; non-completed ones show job
 * status.
 *
 * Three states this must keep APART, because they mean different things to an
 * analyst (ADR 0020 §6):
 * - no investigation ever ran → "No Investigation"
 * - one ran to completion and reached no verdict → "No Verdict"
 * - one is still going, or failed → its run status
 *
 * @param investigation - Latest investigation summary from the case-list API.
 */
export function getInvestigationChipMeta(
	investigation: InvestigationSummary | null,
): InvestigationChipMeta {
	if (!investigation) {
		return {
			label: 'No Investigation',
			tone: 'neutral',
			confidencePct: null,
			sortValue: '0 No Investigation',
		}
	}

	if (investigation.status === 'completed') {
		const { conclusion } = investigation

		// One guard for the whole conclusion — `verdict` and `confidence` are
		// present or absent together (the adapter's gate), so no verdict means no
		// score to be confident about either. ADR 0020 §6 has the full reasoning.
		if (!conclusion) return { ...NO_VERDICT_CHIP_META, confidencePct: null }

		return {
			...VERDICT_CHIP_META[conclusion.verdict],
			confidencePct: investigationConfidencePct(conclusion.confidence),
		}
	}

	const meta = RUN_STATUS_META[investigation.status]
	if (meta) return { ...meta, confidencePct: null }

	return {
		label: toDisplayName(investigation.status),
		tone: 'neutral',
		confidencePct: null,
		sortValue: `8 ${toDisplayName(investigation.status)}`,
	}
}
